import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Button } from './ui/button';

const VideoCarousel = ({ currentLang }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const videos = [
    {
      id: 1,
      vimeoId: '1197192715',
      hash: '8c24ccef9a',
      title: {
        en: 'The Secret to Being a Great Podcast Guest',
        de: 'Das Geheimnis eines großartigen Podcast-Gastes'
      }
    },
    {
      id: 2,
      vimeoId: '1197192713',
      hash: '18e603ddb2',
      title: {
        en: 'How to Prepare for Your First Recording',
        de: 'So bereitest du dich auf deine erste Aufnahme vor'
      }
    },
    {
      id: 3,
      vimeoId: '1197192712',
      hash: 'f1f469908e',
      title: {
        en: 'Why Video Podcasts Are the Future',
        de: 'Warum Video-Podcasts die Zukunft sind'
      }
    },
    {
      id: 4,
      vimeoId: '1197191395',
      hash: 'c39954298c',
      title: {
        en: 'Building Your Personal Brand Through Podcasting',
        de: 'Deine persönliche Marke durch Podcasting aufbauen'
      }
    }
  ];

  const content = {
    en: {
      title: 'Quick Tips',
      subtitle: 'Podcast insights in 60 seconds'
    },
    de: {
      title: 'Schnelle Tipps',
      subtitle: 'Podcast-Einblicke in 60 Sekunden'
    }
  };

  const t = content[currentLang];

  const scrollTo = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = 280 + 16; // card width + gap
    const newIndex = direction === 'left' 
      ? Math.max(0, currentIndex - 1)
      : Math.min(videos.length - 1, currentIndex + 1);
    
    setCurrentIndex(newIndex);
    container.scrollTo({
      left: newIndex * cardWidth,
      behavior: 'smooth'
    });
  };

  return (
    <section className="py-12 px-6 bg-gray-900" aria-labelledby="video-carousel-heading">
      <div className="max-w-7xl mx-auto">
        <header className="flex items-center justify-between mb-8">
          <div>
            <h2 id="video-carousel-heading" className="text-2xl md:text-3xl font-bold text-white mb-2">
              {t.title}
            </h2>
            <p className="text-gray-400">{t.subtitle}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => scrollTo('left')}
              disabled={currentIndex === 0}
              className="border-gray-600 text-white hover:bg-gray-800 disabled:opacity-30"
              aria-label="Previous video"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => scrollTo('right')}
              disabled={currentIndex === videos.length - 1}
              className="border-gray-600 text-white hover:bg-gray-800 disabled:opacity-30"
              aria-label="Next video"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </header>

        <div 
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {videos.map((video, index) => (
            <div 
              key={video.id}
              className="flex-shrink-0 w-[280px]"
              data-testid={`video-card-${index}`}
            >
              <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-gray-800 shadow-lg group">
                <iframe
                  src={`https://player.vimeo.com/video/${video.vimeoId}?h=${video.hash}&badge=0&autopause=0&player_id=0&app_id=58479&background=0`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  className="absolute inset-0 w-full h-full"
                  title={video.title[currentLang]}
                  loading="lazy"
                ></iframe>
              </div>
              <h3 className="mt-3 text-white font-semibold text-sm leading-tight line-clamp-2">
                {video.title[currentLang]}
              </h3>
            </div>
          ))}
        </div>

        {/* Mobile scroll indicator */}
        <div className="flex justify-center gap-2 mt-4 md:hidden">
          {videos.map((_, index) => (
            <div 
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? 'bg-yellow-400' : 'bg-gray-600'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default VideoCarousel;
